import * as React from 'react';
import * as I18n from 'i18n-js';
import * as classNames from 'classnames';
import track, { TrackingProp } from 'react-tracking';

import { IEventData } from '../tracker';
import ConditionDisplay from './condition_display';
import PriceDisplay from './price_display';
import PriceWithShipping from './price_with_shipping';
import ImageCarousel from './image_carousel';
import RibbonView from './ribbon_view';
import {
  Listing,
  core_apimessages_Listing_ListingType as ListingType,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import CreateOfferButton from '../offers/create_offer_button';
import WatchBadgeWithRQL from './watch_badge';
import CoreLink from './core_link';
import { ITEM_WITH_SLUG_ROUTE, newDashboardMessagesPath } from '../url_helpers';
import { withUserContext, IUserContext } from './user_context_provider';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import SellerInfo from './seller_info';

const DIGITAL_LISTING_UUID = 'e71b6e43-7217-4746-b4e3-64bf708a2664';

interface IProps {
  listing?: Listing;
  position?: number;
  inMobileApp?: boolean;
  soldListing?: boolean;
  footerContent?: React.ReactNode;
  displayMetadataAsOffer?: boolean;
  tracking?: TrackingProp;
  onOfferSuccess?: Function;
  expandedCard?: boolean;
  trackingName?: string;
  displayAddToCart: boolean;
}

const DISPLAY_STYLE = 'squareCard';

@track((props: IProps): IEventData => {
  return {
    mParticleInteraction: (data: IEventData) => {
      return buildListingClickEvent({
        listing: props.listing,
        componentName: props.trackingName || data.componentName,
        displayStyle: DISPLAY_STYLE,
        position: data.position,
      });
    },
  };
})
export class ListingCard extends React.Component<IProps & IUserContext, null> {

  auction() {
    return this.props.listing.listingType === ListingType.AUCTION;
  }

  freeExpeditedShipping() {
    return !!this.props.listing.shipping?.freeExpeditedShipping;
  }

  freeShipping() {
    return !!(this.props.listing.shipping?.shippingPrices?.[0]?.rate.amountCents === 0);
  }

  isDigitalListing() {
    if (!this.props.listing.categoryUuids) { return false; }

    return this.props.listing.categoryUuids.includes(DIGITAL_LISTING_UUID);
  }

  localPickupOnly() {
    return !!this.props.listing.shipping?.localPickupOnly;
  }

  listingUrl() {
    if (this.props.soldListing) {
      return (
        ITEM_WITH_SLUG_ROUTE.expand({
          slug: this.props.listing.slug,
          id: this.props.listing.id,
          show_sold: true,
        })
      );
    }

    return (
      ITEM_WITH_SLUG_ROUTE.expand({
        slug: this.props.listing.slug,
        id: this.props.listing.id,
        bk: this.props.listing.bumpKey?.key,
      })
    );
  }

  renderConditionDisplay() {
    if (this.isDigitalListing()) return;

    return (
      <div className="grid-card__condition">
        <ConditionDisplay
          slug={this.props.listing.condition.conditionSlug}
          displayName={this.props.listing.condition.displayName}
        />
      </div>
    );
  }

  renderBumpInfo() {
    if (!this.props.listing.bumpInfo) { return null; }

    return (
      <div className="mt-half opacity-60 size-60 weight-normal">
        {I18n.t('discovery.bumpInfo', { ...this.props.listing.bumpInfo })}
      </div>
    );
  }

  hasDiscount() {
    return !!this.props.listing.pricing.originalPrice;
  }

  ribbonText() {
    return this.props.listing.pricing.ribbon ?
      this.props.listing.pricing.ribbon.display :
      null;
  }

  renderOriginalPrice() {
    if (this.props.expandedCard || !this.hasDiscount()) { return null; }

    return (
      <div className="grid-card__original-price">
        {this.props.listing.pricing.originalPrice.display}
      </div>
    );
  }

  renderPrice() {
    if (this.props.expandedCard) {
      return (
        <PriceWithShipping
          listing={this.props.listing}
        />
      );
    }

    return (
      <PriceDisplay
        amountCents={this.props.listing.pricing.buyerPrice.amountCents}
        currency={this.props.listing.pricing.buyerPrice.currency}
        display={this.props.listing.pricing.buyerPrice.display}
        auction={this.auction()}
      />
    );
  }

  renderCallout() {
    if (this.props.expandedCard || this.isDigitalListing()) { return null; }

    if (this.freeExpeditedShipping()) {
      return (
        <div
          className="grid-card__free-2-day"
        >
          {I18n.t('cms.freeExpeditedShipping')}
        </div>
      );
    }

    if (this.freeShipping()) {
      return (
        <div
          className="grid-card__free-shipping"
        >
          {I18n.t('commons.freeShipping')}
        </div>
      );
    }

    if (this.localPickupOnly()) {
      return (
        <div
          className="grid-card__local-pickup"
        >
          {I18n.t('commons.localPickupOnly')}
        </div>
      );
    }

    return false;
  }

  renderPlaceholderCard() {
    return (
      <div className="grid-card grid-card--placeholder">
        <div className="grid-card__inner">
          <div className="grid-card__main">
            <div className="grid-card__image" />
            <div className="grid-card__main__text">
              <div className="grid-card__title" />
            </div>
          </div>

          <div className="grid-card__footer">
            <div className="grid-card__footer__pricing">
              <div className="grid-card__price" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderMetadata() {
    if (this.props.displayMetadataAsOffer) {
      return (
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="priceCurrency" content={this.props.listing.pricing.buyerPrice.currency} />
          <meta itemProp="price" content={`${this.props.listing.pricing.buyerPrice.amountCents / 100}`} />
          <meta itemProp="availability" content="http://schema.org/InStock" />
          <meta itemProp="url" content={`http://reverb.com/${this.listingUrl()}`} />
          <meta itemProp="gtin14" content={this.props.listing.upc} />
        </div>
      );
    }
  }

  renderOfferButton() {
    const { listing } = this.props;
    const messageURL = newDashboardMessagesPath(listing.id, listing.shop?.user?.uuid);

    if (listing.listingType !== ListingType.MAKE_AN_OFFER) {
      return (
        <a
          href={messageURL}
          className="button width-100 size-80 mt-half"
        >
          {I18n.t('commons.messageSeller')}
        </a>
      );
    }

    return (
      <CreateOfferButton
        listingId={listing.id}
        listing={listing}
        onOfferSuccess={this.props.onOfferSuccess}
        position={this.props.position}
        bumpKey={listing.bumpKey}
      />
    );
  }

  render() {
    if (!this.props.listing) { return this.renderPlaceholderCard(); }

    const classes = classNames(
      'grid-card',
      { 'grid-card--software': this.isDigitalListing() },
      { 'grid-card--discounted': this.hasDiscount() },
      { 'grid-card--expanded': this.props.expandedCard },
    );

    const images = this.props.listing.images.map(i => (i.source));

    return (
      <div className={classes}>
        <CoreLink
          className="grid-card__inner"
          to={this.listingUrl()}
          entityId={this.props.listing.id}
          position={this.props.position}
        >
          {this.renderMetadata()}
          <div className="grid-card__main">
            <div className="grid-card__image">
              <ImageCarousel
                images={images}
                imgAlt={this.props.listing.title}
              />
              <RibbonView
                auction={this.auction()}
                listingStateDescription={this.props.listing.stateDescription}
                listingStateSlug={this.props.listing.state}
                discountDisplay={this.ribbonText()}
              />
            </div>
            <div className="grid-card__main__text">
              {shouldShowBumpTag(this.props.listing) &&
                <div className="grid-card__bump-tag">
                  <BumpTag />
                </div>
              }
              <h4 className="grid-card__title">
                {this.props.listing.title}
              </h4>
              {this.props.expandedCard && this.renderConditionDisplay()}
              {this.renderBumpInfo()}
            </div>
          </div>
          <div className="grid-card__footer">
            {this.renderCallout()}
            {this.renderOriginalPrice()}
            <div
              className="grid-card__footer__pricing"
            >
              {this.props.expandedCard &&
                <div className="mb-half">
                  <SellerInfo
                    listing={this.props.listing}
                  />
                </div>
              }
              <div className="grid-card__price">
                {this.renderPrice()}
              </div>
              {!this.props.expandedCard && this.renderConditionDisplay()}
            </div>
            {(this.props.expandedCard || this.props.displayAddToCart) &&
              <div
                className="grid-card__footer__actions"
              >
                <AddToCartButton
                  listing={this.props.listing}
                  className="width-100"
                  position={this.props.position}
                />
                {this.props.expandedCard && this.renderOfferButton()}
              </div>
            }
            {this.props.footerContent}
          </div>
        </CoreLink>
        <div className="grid-card__watch">
          <WatchBadgeWithRQL listing={this.props.listing} />
        </div>
      </div>
    );
  }
}

export default withUserContext(ListingCard);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/listing_card.tsx